const wait = duration => new Promise(resolve => { setTimeout(resolve, duration) })

class IntervalBackOff {
  constructor(callback, options) {
    this.callback = callback
    this.options = {
      delayInitialExecution: false,
      growthRate: 1.15,
      linearScalar: 1000,
      maximumDelay: 60000,
      maximumIterations: Infinity,
      ...options,
    }
    this.iterationCount = 0
    this.shouldCancel = false
  }

  async execute() {
    if (this.iterationCount < this.options.maximumIterations && !this.shouldCancel) {
      if (!(this.options.delayInitialExecution && this.iterationCount === 0)) {
        await this.callback()
      }

      const duration = Math.min(
        this.options.growthRate ** this.iterationCount * this.options.linearScalar,
        this.options.maximumDelay
      )
      await wait(duration)

      this.iterationCount += 1
      this.execute()
    }
  }

  resetIterationCount() {
    this.iterationCount = 0
  }

  cancel() {
    this.shouldCancel = true
  }
}

/**
 * Repeatedly execute callback with an exponential growing delay
 * between calls.
 *
 * @param {Function} callback - Function to call repeatedly
 * @param {Object} [options] - Additional configuration
 * @param {Number} [options.growthRate] - Exponential rate of growth
 * @param {Number} [options.linearScalar] - Linear scalar on value (think value in milliseconds)
 * @param {Number} [options.maximumDelay] - Limit delay between callback executions (in milliseconds)
 * @param {Number} [options.maximumIterations] - Stop callback execution after N iterations
 * @return {Function} Function that cancels future calls
 */
export default function setIntervalBackOff(
  callback,
  options,
) {
  const backoff = new IntervalBackOff(callback, options)
  backoff.execute()

  return {
    cancel() {
      backoff.cancel()
    },
    resetIterationCount() {
      backoff.resetIterationCount()
    },
  }
}
